<template>
    <data-table
        :id="state_identifier"
        :columns="visible_columns"
        :rows="filtered_rows"
        :search="state.search"
        :total="state.total"
        :found="state.found"
        :current-page="state.page"
        :total-pages="nb_pages"
        :searchable="false"
        table-class="uk-table-condensed uk-table-hover lam-subrecords-list"
        empty-title="No projects found"
        @sort="sort_by"
        @search="search"
        @page="goto_page"
        @click="record_edit(entity, $event.id)"
    >
        <template
            v-slot:controls
        >
            <div class="lam-list-filters">
                <ul slot="filters" class="uk-tab lam-filters">
                    <li :class="{'uk-active': state.filter==='starred'}">
                        <a href @click.prevent="filter('status_label', 'starred')"><open-icon glyph="star-o" /></a>
                    </li>
                    <li :class="{'uk-active': !state.filter}">
                        <a href @click.prevent="filter('', '')">All</a>
                    </li>
                    <li v-if="active" :class="{'uk-active': state.filter==='pic'}">
                        <a href @click.prevent="filter('role', 'pic')">PIC</a>
                    </li>
                    <li v-if="active" :class="{'uk-active': state.filter==='pm'}">
                        <a href @click.prevent="filter('role', 'pm')">PM</a>
                    </li>
                    <li v-if="active" :class="{'uk-active': state.filter==='d'}">
                        <a href @click.prevent="filter('role', 'd')">D</a>
                    </li>
                    <li v-if="active" :class="{'uk-active': state.filter==='active'}">
                        <a href @click.prevent="filter('project_status', 'active')">Active</a>
                    </li>
                    <li v-if="!active" :class="{'uk-active': state.filter==='0-30 days'}">
                        <a href @click.prevent="filter('current_state', '0-30 days')">0-30</a>
                    </li>
                    <li v-if="!active" :class="{'uk-active': state.filter==='30-60 days'}">
                        <a href @click.prevent="filter('current_state', '30-60 days')">30-60</a>
                    </li>
                    <li v-if="!active" :class="{'uk-active': state.filter==='60-90 days'}">
                        <a href @click.prevent="filter('current_state', '60-90 days')">60-90</a>
                    </li>
                    <li v-if="!active" :class="{'uk-active': state.filter==='Chosen-Pending'}">
                        <a href @click.prevent="filter('current_state', 'Chosen-Pending')">Chosen-Pending</a>
                    </li>
                    <li v-if="!active" :class="{'uk-active': state.filter==='Assumed Dead'}">
                        <a href @click.prevent="filter('current_state', 'Assumed Dead')">Assumed Dead</a>
                    </li>
                    <li v-if="!active" :class="{'uk-active': state.filter==='Dead'}">
                        <a href @click.prevent="filter('current_state', 'Dead')">Dead</a>
                    </li>
                    <li v-if="active" :class="{'uk-active': state.filter==='on-hold'}">
                        <a href @click.prevent="filter('current_state', 'on-hold')">On-Hold</a>
                    </li>
                    <li v-if="active" :class="{'uk-active': state.filter==='completed'}">
                        <a href @click.prevent="filter('current_state', 'completed')">Completed</a>
                    </li>
                    <li v-if="active" :class="{'uk-active': state.filter==='dead'}">
                        <a href @click.prevent="filter('current_state', 'dead')">Dead</a>
                    </li>
                </ul>
            </div>
        </template>
        <template
            v-slot:summary="{ row }"
        >
            <h6>
                <favorite-input
                    name="favorite"
                    v-model="row.favorite"
                    @input="save_favorite(row, ...arguments)"
                />
                {{ row.name }}
            </h6>
        </template>
        <template
            v-slot:tbody="{ row, field }"
        >
            <div
                v-if="field === 'favorite'"
                class="uk-form-controls-text uk-text-center"
            >
                <favorite-input
                    name="favorite"
                    v-model="row.favorite"
                    @input="save_favorite(row, ...arguments)"
                />
            </div>
            <span v-else-if="field === 'role'">{{ role_for_project(row) }}</span>
            <span v-else-if="field === 'sortable_number'">{{ row.sortable_number | lam.project-number }}</span>
            <span v-else-if="field === 'proposal_amount'">
                <span v-if="!row.proposal_amount" class="uk-text-warning"><open-icon glyph="close" title="Proposal not sent" /></span>
                <span v-else class="uk-text-success"><open-icon glyph="check-circle-o" title="Proposal sent" /></span>
            </span>
            <span v-else-if="field === 'projected_awarded_at_as_string'">
                <span v-if="!!row.projected_awarded_at_as_string">
                    {{ row.projected_awarded_at_as_string | nibnut.date("MM/DD/YY") }}
                </span>
                <span v-else class="uk-text-muted">-</span>
            </span>
            <span v-else-if="field === 'active_phase_constant'">
                <span v-if="!!row.status_label && !!row.status_label.match(/hold/i)">Hold</span>
                <span v-else-if="!!row.status_label && !!row.status_label.match(/complete/i)">Completed</span>
                <span v-else-if="!!row.status_label && !!row.status_label.match(/dead/i)">Dead</span>
                <span v-else>{{ row.active_phase_constant }}</span>
            </span>
            <span v-else-if="field === 'computed_next_deadline_due_on_as_string'">
                {{ row.computed_next_deadline_due_on_as_string | nibnut.date("MM/DD/YY") }}
            </span>
            <router-link
                v-else-if="field === 'name'"
                :to="{ name: `${!!row.activated_at?'project':'prospective_project'}.edit`, params: { id: row.id } }"
            >
                {{ row.name }}
            </router-link>
            <span v-else-if="field === 'days_since_received'">
                <span v-if="row[field] >= 60">{{ (row[field] / 30) | nibnut.number("0,0") }}&nbsp;months</span>
                <span v-else>{{ row[field] | nibnut.number("0,0") }}&nbsp;days</span>
            </span>
            <div v-else-if="field === 'computed_spent'" class="uk-text-right">
                $&nbsp;{{ row.computed_fee - row.computed_spent | nibnut.number("0,0") }}
                <div>
                    <small>({{ (row.computed_fee - row.computed_spent) | lam.percent(row.computed_fee) }}&nbsp;%)</small>
                </div>
            </div>
            <span v-else-if="field === 'user_hours_for_the_week'">{{ row.user_hours_for_the_week | nibnut.number("0,0.0") }}</span>
            <span v-else>{{ row[field] }}</span>
       </template>
    </data-table>
</template>

<script type="text/javascript">
import dashboard_card from "./dashboard_card"
import is_remote_data_table_source from "../../mixins/IsRemoteDataTableSource"
import project_utilities from "../../mixins/ProjectUtilities"
import handles_saving from "../../mixins/HandlesSaving"

import FavoriteInput from "../../ui/FavoriteInput"
import OpenIcon from "../../ui/OpenIcon"

export default {
    mixins: [dashboard_card, is_remote_data_table_source, project_utilities, handles_saving],
    components: {
        FavoriteInput,
        OpenIcon
    },
    watch: {
        active: "reset_state"
    },
    methods: {
        post_load (response) {
        },
        reset_state (force) {
            if(force || !this.stored_state) {
                this.set_state_values({
                    sort_by: this.active ? "sortable_number" : "days_since_received",
                    sort_dir: "desc",
                    context: `dashboard-${this.active ? "active" : "prospective"}`,
                    activated_at: this.active
                })
            }
        },
        save_favorite (row, favorite) {
            this.save_field_for_record_id("project", row.id, favorite, "favorite")
        },
        filter_rows (rows) {
            if(this.state.filter_on && this.state.filter) {
                return rows.filter(row => {
                    if(this.state.filter_on === "role") {
                        const role = this.role_for_project(row).toLowerCase()
                        return role === this.state.filter
                    }

                    if(this.state.filter === "starred") return !!row.favorite
                    if(this.state.filter_on === "project_status") {
                        return row.project_status === this.state.filter
                    } else {
                        return row[this.state.filter_on] === this.state.filter
                    }
                })
            }
            return rows
        }
    },
    computed: {
        state_identifier () {
            return `Dashboard-${this.card.id}`
        },
        visible_columns () {
            const columns = {}
            if(!this.state.filter || !this.state.filter.match(/^starred$/)) columns.favorite = this.columns.favorite

            if(this.active) {
                if(!this.state.filter || !this.state.filter.match(/^(pic|pm|d)$/)) columns.role = this.columns.role
                columns.sortable_number = this.columns.sortable_number
                columns.name = this.columns.name
                columns.active_phase_constant = this.columns.active_phase_constant
                columns.computed_next_deadline = this.columns.computed_next_deadline
                columns.computed_next_deadline_due_on_as_string = this.columns.computed_next_deadline_due_on_as_string
                columns.computed_spent = this.columns.computed_spent
                columns.user_hours_for_the_week = this.columns.user_hours_for_the_week
            } else {
                columns.name = this.columns.name
                if(this.is_business_manager) {
                    columns.pic_id = this.columns.pic_id
                    columns.pm_id = this.columns.pm_id
                    columns.last_rfm_activity = this.columns.last_rfm_activity
                } else {
                    columns.current_state = this.columns.current_state
                }
                columns.proposal_amount = this.columns.proposal_amount
                columns.projected_awarded_at_as_string = this.columns.projected_awarded_at_as_string
                columns.days_since_received = this.columns.days_since_received
            }

            return columns
        }
    },
    props: {
        active: {
            type: Boolean,
            required: true
        }
    },
    data () {
        return {
            entity: "project",
            columns: {
                favorite: { label: " ", sort: false, type: "plain", cell_class: { "lam-col-star": true } },
                role: { label: "Role", sort: false, type: "plain", cell_class: { "lam-col-user-initials": true } },
                sortable_number: { label: "Number", sort: null, type: "plain", head_class: { "uk-text-center": true }, cell_class: { "uk-text-center lam-col-number": true } },
                name: { label: "Project", sort: null, type: "plain", cell_class: { "lam-col-project-name": true } },
                active_phase_constant: { label: "Phase", sort: false, type: "plain", cell_class: { "uk-text-center lam-col-project-phase": true } },
                computed_next_deadline: { label: "Deadline", sort: null, type: "plain" },
                computed_next_deadline_due_on_as_string: { label: "Due", sort: null, type: "plain", cell_class: { "lam-col-start": true } },
                computed_spent: { label: "Remain.", sort: null, type: "plain", head_class: { "uk-text-right": true }, cell_class: { "lam-col-fee-remaining": true } },
                computed_fee: { label: "Total Fee", sort: null, type: "plain", cell_class: { "lam-col-fee": true } },
                pic_id: { label: "PIC", sort: false, type: "plain", cell_class: { "lam-col-user-initials": true } },
                pm_id: { label: "PM", sort: false, type: "plain", cell_class: { "lam-col-user-initials": true } },
                last_rfm_activity: { label: "Latest Log", sort: null, type: "plain", cell_class: { "lam-col-rfm-activity": true } },
                project_status: { label: "Status", sort: false, type: "plain" },
                shelf_life: { label: "Status", sort: null, type: "plain" },
                current_state: { label: "Status", sort: null, type: "plain" },
                days_since_received: { label: "Age", sort: null, type: "plain", cell_class: { "lam-col-date-relative": true } },
                proposal_amount: { label: "Proposal", sort: false, type: "plain" },
                projected_awarded_at_as_string: { label: "Award", sort: null, type: "plain" },
                status_label: { label: "Status Label", sort: null, type: "plain" },
                user_hours_for_the_week: { label: "Hrs", sort: null, type: "plain", cell_class: { "uk-text-center": true } }
            },

            default_state: {
                per_page: 10,
                page: 1,
                sort_by: null,
                sort_dir: null,
                filter_on: "project_status",
                filter: "starred",
                archived: false,
                search: "",
                total: 0,
                found: 0,
                context: "dashboard",
                activated_at: 0,
                include_dead: true
            }
        }
    }
}
</script>
